import React from "react";

import {
  LANDING_DRAWING_FADE_IN_MS,
  LANDING_DRAWING_FADE_OUT_MS,
  LANDING_EGG_MOVE_MS,
  LANDING_HOLD_MS,
  LANDING_INITIAL_SETTLE_MS,
  LANDING_PHOTO_FADE_IN_MS,
  LANDING_PHOTO_FADE_OUT_MS,
} from "../constants";

// Drives the landing-page marc reel. Per cycle, with t=0 at the moment
// `currentIndex` (just) became the current marc:
//
//   t = 0                 NEW marc photo starts fading in (against
//                         the still-old egg pose). On the very first
//                         cycle, an initial settle is used in place
//                         of the photo fade-in delay below.
//   t = PHOTO_FADE_IN     Egg starts tweening to NEW marc's pose
//                         (eggIndex catches up to currentIndex).
//   t = + EGG_MOVE        Drawing of the NEW marc fades in.
//   t = + DRAW_FADE_IN    Fully visible. Hold begins.
//   t = + HOLD            Drawing + photo start fading out.
//   t = + DRAW_FADE_OUT   Advance currentIndex; effect re-fires and
//                         the next cycle starts at its own t=0.
//
// The staggered ordering — photo fades in BEFORE the egg moves —
// reads as a comedic beat: "Here's the new Marc. (now the egg slides
// into place to become him.) Now the drawing reveals." Egg lighting
// snaps at the eggIndex bump, while the drawing is hidden and the
// photo is the only thing in the focal area.
//
// **Entry from another mode** (creative/speedrun → landing via title
// click): when `entryFromMarcId` is non-null and != ids[0] on the
// first activation, the cold-boot settle is replaced with a full
// fade-out → swap → fade-in → egg-move sequence. The photo's src and
// the egg's pose are held at the FROM marc through the appropriate
// phases (see `entryPhotoHold` / `entryEggHold`) so the fade-out
// shows the OLD marc and the fade-in shows the NEW marc — same shape
// as a regular subsequent-cycle transition. Without this, the photo
// would snap to ids[0] immediately and the user would see ids[0]
// fading 1→0 before the cycle's natural fade-in.
//
// Single-marc edge case: if `ids.length <= 1`, fade in the lone
// marc's photo+drawing once and hold forever.
export default function useLandingCycle({ ids, active, entryFromMarcId }) {
  const [index, setIndex] = React.useState(0);
  // eggIndex lags currentIndex by PHOTO_FADE_IN_MS within each cycle.
  // Equal to currentIndex outside the lag window.
  const [eggIndex, setEggIndex] = React.useState(0);
  const [drawingShown, setDrawingShown] = React.useState(false);
  const [photoShown, setPhotoShown] = React.useState(false);
  // Held FROM marc during an entry-from-other-mode transition. Two
  // separate holds because the photo's src and the egg's pose lift
  // at different times in the entry timeline:
  //   - entryPhotoHold lifts after PHOTO_FADE_OUT (photo reaches
  //     opacity 0; src can swap under cover).
  //   - entryEggHold lifts after PHOTO_FADE_OUT + PHOTO_FADE_IN
  //     (photo fully visible at NEW src; egg starts tweening).
  // Both null outside the entry window.
  const [entryPhotoHold, setEntryPhotoHold] = React.useState(null);
  const [entryEggHold, setEntryEggHold] = React.useState(null);
  const isFirstCycleRef = React.useRef(true);
  // Snapshot at activation so a stale prop doesn't disturb a
  // mid-cycle re-render.
  const entryFromAtActivationRef = React.useRef(null);

  React.useEffect(() => {
    if (!active) {
      setIndex(0);
      setEggIndex(0);
      setDrawingShown(false);
      setPhotoShown(false);
      setEntryPhotoHold(null);
      setEntryEggHold(null);
      isFirstCycleRef.current = true;
      entryFromAtActivationRef.current = null;
    }
  }, [active]);

  // Snapshot the FROM marc and seed entry state synchronously on
  // first activation. useLayoutEffect (not useEffect) so seeded
  // state is in place before the browser paints the first landing
  // frame — otherwise the photo briefly snaps to ids[0] at high
  // opacity before any fade can hide it.
  //
  // Two seeded paths, depending on whether the FROM marc differs
  // from ids[0]:
  //
  //   FROM != ids[0]  — set entryPhotoHold/entryEggHold so the
  //                     photo's src and the egg's pose stay pinned
  //                     to FROM through the fade-out / fade-in /
  //                     egg-move beats (driven by the cycle effect).
  //   FROM == ids[0]  — the photo is already showing the right marc
  //                     at opacity 1 (we came from a mode that was
  //                     rendering ids[0] in the background). Pre-set
  //                     photoShown=true and drawingShown=true so the
  //                     first landing render commits at opacity 1 —
  //                     no CSS transition fires (Frame K → Frame K'
  //                     happens via the synchronous re-render from
  //                     this effect, no paint between, browser sees
  //                     opacity stayed at 1 from the prior mode's
  //                     paint). The cycle effect's cold-boot path
  //                     still runs but its setPhotoShown(true) at
  //                     SETTLE_MS and setDrawingShown(true) at
  //                     fade-in time become no-ops, leaving us in a
  //                     "fully visible, waiting for hold to expire"
  //                     state until the cycle naturally fades out
  //                     and advances.
  React.useLayoutEffect(() => {
    if (!active) return;
    if (!isFirstCycleRef.current) return;
    if (ids.length === 0) return;
    entryFromAtActivationRef.current = entryFromMarcId ?? null;
    if (entryFromMarcId == null) return;
    if (entryFromMarcId === ids[0]) {
      setPhotoShown(true);
      setDrawingShown(true);
    } else {
      setEntryPhotoHold(entryFromMarcId);
      setEntryEggHold(entryFromMarcId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  React.useEffect(() => {
    if (!active) return;
    if (ids.length === 0) return;

    const timers = [];
    const at = (delay, fn) => timers.push(setTimeout(fn, delay));

    const isFirst = isFirstCycleRef.current;
    isFirstCycleRef.current = false;
    const fromMarcId = entryFromAtActivationRef.current;
    const useEntry =
      isFirst && fromMarcId != null && fromMarcId !== ids[0];

    let drawingFadeInAt;
    if (useEntry) {
      // Entering from creative/speedrun: the photo currently shows
      // FROM marc at opacity 1 with the egg in FROM pose. Fade out
      // the FROM photo (entryPhotoHold keeps src/transform pinned),
      // swap to ids[0] under cover of opacity 0, fade back in over
      // the still-OLD egg pose, then move the egg, then fade in the
      // drawing.
      const photoFadedOutAt = LANDING_PHOTO_FADE_OUT_MS;
      at(photoFadedOutAt, () => {
        setEntryPhotoHold(null);
        setPhotoShown(true);
      });
      const eggMoveAt = photoFadedOutAt + LANDING_PHOTO_FADE_IN_MS;
      at(eggMoveAt, () => {
        setEntryEggHold(null);
      });
      drawingFadeInAt = eggMoveAt + LANDING_EGG_MOVE_MS;
    } else if (isFirst) {
      // First marc has no prior egg pose to move from — settle, fade
      // in photo, fade in drawing.
      at(LANDING_INITIAL_SETTLE_MS, () => setPhotoShown(true));
      drawingFadeInAt =
        LANDING_INITIAL_SETTLE_MS + LANDING_PHOTO_FADE_IN_MS;
      // eggIndex stays at 0 (== index); no egg move on first cycle.
    } else {
      // Subsequent marcs: photo fades in first against the still-old
      // egg pose, then the egg slides over to the new pose, then the
      // drawing fades in.
      at(0, () => setPhotoShown(true));
      at(LANDING_PHOTO_FADE_IN_MS, () => setEggIndex(index));
      drawingFadeInAt =
        LANDING_PHOTO_FADE_IN_MS + LANDING_EGG_MOVE_MS;
    }
    at(drawingFadeInAt, () => setDrawingShown(true));

    if (ids.length <= 1) {
      return () => timers.forEach(clearTimeout);
    }

    const drawingFadeInDoneAt = drawingFadeInAt + LANDING_DRAWING_FADE_IN_MS;
    const fadeOutStartsAt = drawingFadeInDoneAt + LANDING_HOLD_MS;
    const advanceAt = fadeOutStartsAt + LANDING_DRAWING_FADE_OUT_MS;

    at(fadeOutStartsAt, () => {
      setDrawingShown(false);
      setPhotoShown(false);
    });
    at(advanceAt, () => {
      setIndex((i) => (i + 1) % ids.length);
    });

    return () => timers.forEach(clearTimeout);
  }, [active, index, ids.length]);

  const currentId =
    entryPhotoHold ?? (ids.length > 0 ? ids[index % ids.length] : null);
  const eggMarcId =
    entryEggHold ?? (ids.length > 0 ? ids[eggIndex % ids.length] : null);
  return { currentId, eggMarcId, drawingShown, photoShown };
}
